import {
  IonBadge,
  IonCardSubtitle,
  IonIcon,
  IonItem,
  IonLabel,
  IonSpinner,
  useIonActionSheet,
  useIonAlert,
} from '@ionic/react'
import { msg, plural } from '@lingui/core/macro'
import { useLingui } from '@lingui/react'
import { businessOutline, cloudOfflineOutline } from 'ionicons/icons'
import { useQuery } from 'urql'
import { useSyncStatus } from '../context/SyncStatusProvider'
import { MyOrganizationsQuery } from '../graphql/organizations'
import { useOrgSwitch } from '../hooks/useOrgSwitch'
import { useOrganization } from '../hooks/useOrganization'

/**
 * Profile row for switching organizations. Hidden for single-org operators.
 *
 * Switching exchanges tokens and reloads the app, so it is fully blocked while
 * offline and gated behind a "sync first" prompt while offline mutations are
 * still queued (they belong to the current org and would be lost).
 */
export const OrgSwitcherItem = () => {
  const { _ } = useLingui()
  const [{ data }] = useQuery({ query: MyOrganizationsQuery })
  const { organization } = useOrganization()
  const { activeTid, blockedReason, switching, switchTo } = useOrgSwitch()
  const { pendingCount, forceSync } = useSyncStatus()
  const [presentActionSheet] = useIonActionSheet()
  const [presentAlert] = useIonAlert()

  const organizations = data?.myOrganizations ?? []
  if (organizations.length <= 1) return null

  const offline = blockedReason === 'offline'
  const pendingSync = blockedReason === 'pending-sync'
  const syncing = blockedReason === 'syncing'
  const enabled = !offline && !syncing && !switching

  const confirmSwitch = (tid: number, name: string) => {
    presentAlert({
      header: _(msg`Switch to ${name}?`),
      message: _(msg`The app will reload with the selected organization.`),
      buttons: [
        { text: _(msg`Cancel`), role: 'cancel' },
        {
          text: _(msg`Switch`),
          handler: () => {
            switchTo(tid)
          },
        },
      ],
    })
  }

  const openOrgSheet = () => {
    presentActionSheet({
      header: _(msg`Select Organization`),
      buttons: [
        ...organizations.map((org) => ({
          text: org.name,
          cssClass: org.id === activeTid ? 'action-sheet-default' : undefined,
          handler: () => {
            if (org.id !== activeTid) confirmSwitch(org.id, org.name)
          },
        })),
        { text: _(msg`Cancel`), role: 'cancel' },
      ],
    })
  }

  const promptSyncFirst = () => {
    presentAlert({
      header: _(msg`Sync before switching`),
      message: _(
        plural(pendingCount, {
          one: '# change is waiting to sync. Sync before switching organizations.',
          other:
            '# changes are waiting to sync. Sync before switching organizations.',
        }),
      ),
      buttons: [
        { text: _(msg`Cancel`), role: 'cancel' },
        {
          text: _(msg`Sync now`),
          handler: () => {
            forceSync()
          },
        },
      ],
    })
  }

  const handleClick = () => {
    if (!enabled) return
    if (pendingSync) {
      promptSyncFirst()
      return
    }
    openOrgSheet()
  }

  return (
    <IonItem
      button={enabled}
      detail={enabled}
      onClick={handleClick}
      data-testid='org-switcher-item'
    >
      <IonIcon
        icon={offline ? cloudOfflineOutline : businessOutline}
        slot='start'
      />
      <IonLabel>
        {_(msg`Organization`)}
        <IonCardSubtitle style={{ marginTop: 4, fontSize: '0.8em' }}>
          {offline ? _(msg`Unavailable offline`) : organization?.name}
        </IonCardSubtitle>
      </IonLabel>
      {syncing || switching ? (
        <IonSpinner slot='end' name='crescent' />
      ) : pendingSync ? (
        <IonBadge slot='end' color='warning'>
          {pendingCount}
        </IonBadge>
      ) : null}
    </IonItem>
  )
}
